import React, { useEffect, useState } from "react";
import { svg } from "../../../Assets/svg";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Spin } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch } from "../../../store";
import { BtnLoader } from "../../../Components/Components";
import {
  getPriceSheetItemsListAPI,
  updatePriceSheetAPI,
} from "./priceSheetApis";

const UpdatePriceSheetVariables = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialValues = {
    uprightSteel: "",
    bracingSteel: "",
    beamSteel: "",
    anchorPlateSteel: "",
    recoveryRate: "",
    paintPrice: "",
    scrapRate: "",
    boltPrice: "",
    boltPriceM12: "",
    boltPriceM10_90: "",
    boltPriceM10_20: "",
    boltPriceM8_35: "",
    boltPerFootplatePrice: "",
    anchorPrice: "",
    spacerPrice: "",
    profileRate82: "",
    profileRate100: "",
    profileRate120: "",
    bracingRate: "",
    meLineRate: "",
    cbLineRate: "",
    obLineRate: "",
    hotDipGalvanising: "",
    lsUprightRate: "",
    sheetSteelPrice: "",
    driveInRailsPrice: "",
    driveInRailsSteelPrice: "",
    boltPriceM8_16: "",
    boltPriceM8_50_thunder_bolts: "",
    shelving_galv_plate_price: "",
    shelving_cr_plate_price: "",
    boltless_upright_steel_price: "",
    endframe_bipoles_height_price: "",
    monopoles_uprights_price: "",

    // Component Targets
    rate_me156_small: "",
    rate_me156_large: "",
    rate_me120_small: "",
    rate_me120_large: "",
    rate_me110_small: "",
    rate_me110_large: "",
    rate_cb95_small: "",
    rate_cb95_large: "",
    rate_ob101_small: "",
    rate_ob101_large: "",
    rate_pallet_support: "",
    rate_beams_upto_1_2m: "",
    rate_beams_upto_2_7m: "",
    rate_beams_2_75m_to_3_3m: "",
    rate_beams_3_35m_plus: "",
    rate_cut_backstops: "",
    rate_drill_backstops: "",
    rate_drill_drive_beams: "",
    cd146_angle_1m_to_3m: "",
    cd146_angle_3m_to_4m: "",
    cd123_angle_1m_to_3m: "",
    cd123_angle_3m_to_4m: "",
    cd95_angle_1m_to_3m: "",
    cd95_angle_3m_to_4m: "",
    cd86_angle_1m_to_3m: "",
    cd86_angle_3m_to_4m: "",

    // Shelving Production Rates
    punch_shelves_300_to_450: "",
    punch_shelves_500_pluse: "",
    folding_shelves: "",
    punch_nb_crossties: "",
    fold_nb_crossties: "",
    punch_nb_feet: "",
    fold_nb_feet: "",
    punch_nb_backs: "",
    punch_adjustable_dividers: "",
    punch_fixed_dividers: "",
    fold_adjustable_dividers: "",
    fold_fixed_dividers: "",
  };

  const validationSchema = Yup.object().shape(
    Object.keys(initialValues).reduce((acc: any, key) => {
      acc[key] = Yup.string().required("Required");
      return acc;
    }, {}),
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setSaving(true);

      const numericValues = Object.keys(values).reduce((acc: any, key) => {
        const val = values[key as keyof typeof values];
        acc[key] = val === "" ? 0 : Number(val);
        return acc;
      }, {});

      const payload = {
        id: id,
        ...numericValues,
      };

      dispatch(
        updatePriceSheetAPI({
          payload,
          onSuccess: () => {
            setSaving(false);
            navigate("/price-sheet");
          },
          onError: () => {
            setSaving(false);
          },
        }),
      );
    },
  });

  const fetchDetails = () => {
    if (!id) return;
    setLoading(true);
    dispatch(
      getPriceSheetItemsListAPI({
        price_id: id as string,
        filters: { page: 1, limit: 1 },
        onSuccess: (data: any) => {
          const priceSheetData = data?.priceSheetData;
          if (priceSheetData) {
            const newValues = { ...initialValues };
            Object.keys(initialValues).forEach((key) => {
              if (
                priceSheetData[key] &&
                typeof priceSheetData[key] === "object"
              ) {
                (newValues as any)[key] = priceSheetData[key].value ?? "";
              }
            });
            formik.setValues(newValues);
          }
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const variableCosts = [
    {
      label: "Upright Steel Price",
      name: "uprightSteel",
      unit: "£/Ton",
    },
    {
      label: "Bracing Steel Price",
      name: "bracingSteel",
      unit: "£/Ton",
    },
    { label: "Beam Steel Price", name: "beamSteel", unit: "£/Ton" },
    {
      label: "Anchor Plate Steel Price",
      name: "anchorPlateSteel",
      unit: "£/Ton",
    },
    { label: "Overhead Rate", name: "recoveryRate", unit: "£/hr" },
    { label: "Powder Coating Price", name: "paintPrice", unit: "£/Kg" },
    { label: "Scrap %", name: "scrapRate", unit: "%" },
    {
      label: "M10x65 Bolts&Nuts",
      name: "boltPrice",
      unit: "£/100",
    },
    {
      label: "M12x80 Bolts&Nuts",
      name: "boltPriceM12",
      unit: "£/100",
    },
    {
      label: "M10x90 Bolts&Nuts",
      name: "boltPriceM10_90",
      unit: "£/100",
    },
    {
      label: "M10x20 Bolts&Nuts",
      name: "boltPriceM10_20",
      unit: "£/100",
    },
    {
      label: "M8x35 Bolts&Nuts",
      name: "boltPriceM8_35",
      unit: "£/100",
    },
    {
      label: "M8x16 Bolts&Nuts",
      name: "boltPriceM8_16",
      unit: "£/100",
    },
    {
      label: "M8x50 Thunder Bolts",
      name: "boltPriceM8_50_thunder_bolts",
      unit: "£/100",
    },
    {
      label: "Bolts per Footplate",
      name: "boltPerFootplatePrice",
      unit: "£/pc",
    },
    { label: "Anchor Plates", name: "anchorPrice", unit: "£/each" },
    { label: "Spacers", name: "spacerPrice", unit: "£/1000" },
    {
      label: "82 Uprights m per hour",
      name: "profileRate82",
      unit: "m/hr",
    },
    {
      label: "100 Uprights m per hour",
      name: "profileRate100",
      unit: "m/hr",
    },
    {
      label: "120 Uprights m per hour",
      name: "profileRate120",
      unit: "m/hr",
    },
    {
      label: "Bracing m per hour",
      name: "bracingRate",
      unit: "m/hr",
    },
    {
      label: "ME Line m per hour",
      name: "meLineRate",
      unit: "m/hr",
    },
    {
      label: "CB Line m per hour",
      name: "cbLineRate",
      unit: "m/hr",
    },
    {
      label: "OB Line m per hour",
      name: "obLineRate",
      unit: "m/hr",
    },
    {
      label: "Hot Dip Galvanising",
      name: "hotDipGalvanising",
      unit: "£/Ton",
    },
    {
      label: "LS Upright m per hour",
      name: "lsUprightRate",
      unit: "m/hr",
    },
    {
      label: "Sheet Steel Price",
      name: "sheetSteelPrice",
      unit: "£/Ton",
    },
    {
      label: "Drive In Rail m per hour",
      name: "driveInRailsPrice",
      unit: "m/hr",
    },
    {
      label: "Drive In Rail Steel Price",
      name: "driveInRailsSteelPrice",
      unit: "£/Ton",
    },
    {
      label: "Shelving Galv Plate Price",
      name: "shelving_galv_plate_price",
      unit: "£/per Ton",
    },
    {
      label: "Shelving CR Plate Price",
      name: "shelving_cr_plate_price",
      unit: "£/per Ton",
    },
    {
      label: "Endframe & Bipoles Additional Cost",
      name: "endframe_bipoles_height_price",
    },
    {
      label: "Monopoles & Uprights Additional Cost",
      name: "monopoles_uprights_price",
    },
    {
      label: "Finish Surcharge",
      name: "finishSurcharge",
      unit: "%",
    },
    {
      label: "Custom Color Extra",
      name: "customColorExtra",
      unit: "£",
    },
  ];

  const componentProducts = [
    {
      label: "ME156 / CB146 / ME139 (1m - 2.7m) 1 Man",
      name: "rate_me156_small",
    },
    {
      label: "ME156 / CB146 / ME139 (2.7m - 4m) 2 Men",
      name: "rate_me156_large",
    },
    {
      label: "ME120 / CB123 (1m - 2.7m) 1 Man",
      name: "rate_me120_small",
    },
    {
      label: "ME120 / CB123 (2.7m - 4m) 2 Men",
      name: "rate_me120_large",
    },
    {
      label: "ME110 / CB103 / ME99 (1m - 2.7m) 1 Man",
      name: "rate_me110_small",
    },
    {
      label: "ME110 / CB103 / ME99 (2.7m - 4m) 2 Men",
      name: "rate_me110_large",
    },
    {
      label: "CB95 / ME90 / CB86 (1m - 2.7m) 1 Man",
      name: "rate_cb95_small",
    },
    {
      label: "CB95 / ME90 / CB86 (2.7m - 4m) 2 Men",
      name: "rate_cb95_large",
    },
    {
      label: "OB101 / OB87 / OB60 (1m - 2.7m)",
      name: "rate_ob101_small",
    },
    {
      label: "OB101 / OB87 / OB60 (2.7m - 4m)",
      name: "rate_ob101_large",
    },
    {
      label: "Pallet Support / Chipboard Support (1 Man)",
      name: "rate_pallet_support",
    },
    {
      label: "Beams Upto 1.2m",
      name: "rate_beams_upto_1_2m",
    },
    {
      label: "Beams Upto 2.7m",
      name: "rate_beams_upto_2_7m",
    },
    {
      label: "Beams 2.75m - 3.3m",
      name: "rate_beams_2_75m_to_3_3m",
    },
    {
      label: "Beams 3.35m +",
      name: "rate_beams_3_35m_plus",
    },
    {
      label: "Cut Backstops",
      name: "rate_cut_backstops",
    },
    {
      label: "Drill Backstops",
      name: "rate_drill_backstops",
    },
    {
      label: "Drill Drive Beams",
      name: "rate_drill_drive_beams",
    },
    {
      label: "CD146 Angle 1m - 3m",
      name: "cd146_angle_1m_to_3m",
    },
    {
      label: "CD146 Angle 3m - 4m",
      name: "cd146_angle_3m_to_4m",
    },
    {
      label: "CD123 Angle 1m - 3m",
      name: "cd123_angle_1m_to_3m",
    },
    {
      label: "CD123 Angle 3m - 4m",
      name: "cd123_angle_3m_to_4m",
    },
    {
      label: "CD95 Angle 1m - 3m",
      name: "cd95_angle_1m_to_3m",
    },
    {
      label: "CD95 Angle 3m - 4m",
      name: "cd95_angle_3m_to_4m",
    },
    {
      label: "CD86 Angle 1m - 3m",
      name: "cd86_angle_1m_to_3m",
    },
    {
      label: "CD86 Angle 3m - 4m",
      name: "cd86_angle_3m_to_4m",
    },
    {
      label: "Punch Shelves 300-450",
      name: "punch_shelves_300_to_450",
    },
    {
      label: "Punch Shelves 500+",
      name: "punch_shelves_500_pluse",
    },
    {
      label: "Folding Shelves",
      name: "folding_shelves",
    },
    {
      label: "Punch NB Crossties",
      name: "punch_nb_crossties",
    },
    {
      label: "Fold NB Crossties",
      name: "fold_nb_crossties",
    },
    {
      label: "Punch NB Feet",
      name: "punch_nb_feet",
    },
    {
      label: "Fold NB Feet",
      name: "fold_nb_feet",
    },
    {
      label: "Punch NB Backs",
      name: "punch_nb_backs",
    },
    {
      label: "Punch Adjustable Dividers",
      name: "punch_adjustable_dividers",
    },
    {
      label: "Punch Fixed Dividers",
      name: "punch_fixed_dividers",
    },
    {
      label: "Fold Adjustable Dividers",
      name: "fold_adjustable_dividers",
    },
    {
      label: "Fold Fixed Dividers",
      name: "fold_fixed_dividers",
    },
    {
      label: "Boltless Upright m per hour",
      name: "boltless_upright_steel_price",
    },
  ];

  return (
    <div className="page-content h-100 px-40 py-40 bg-light d-flex flex-column overflow-y-auto">
      <div className="mb-3 d-flex flex-column gap-md-2 gap-0">
        <div className="d-flex flex-md-nowrap flex-wrap gap-sm-3 justify-content-between align-items-center">
          <h2 className="text-black fw-bolder page-title mb-0 d-flex align-items-center gap-2">
            <span
              className="cursor-pointer svg-24"
              onClick={() => navigate(-1)}
            >
              {svg?.backArrow || "←"}
            </span>
            Update Sheet Variables
          </h2>
          <div className="d-md-flex d-none justify-content-end align-items-center gap-3">
            <button
              className="btn btn-lg btn-outline-secondary mob-btn"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              className="btn btn-lg btn-primary mob-btn"
              onClick={() => formik.handleSubmit()}
            >
              {saving ? <BtnLoader /> : "Update"}
            </button>
          </div>
        </div>
      </div>

      <h3 className="fs-4 fw-bold text-black mb-md-3 mb-2">Variable Costs</h3>
      <div className="card border-0 rounded-4 mb-md-4 mb-3 shadow-sm">
        <div className="card-body p-md-4 p-3">
          <div className="row g-md-4 g-3">
            {variableCosts.map((field) => (
              <div className="col-lg-3 col-md-4 col-sm-6" key={field.name}>
                <div className="form-item">
                  <label className="custom-label mb-1 ff">{field.label}</label>
                  <Input
                    name={field.name}
                    className="form-control form-control-lg"
                    value={
                      formik.values[field.name as keyof typeof formik.values]
                    }
                    placeholder="0"
                    prefix={<span className="text-white">{field.unit}</span>}
                    size="large"
                    status={
                      formik.touched[
                        field.name as keyof typeof formik.values
                      ] &&
                      formik.errors[field.name as keyof typeof formik.values]
                        ? "error"
                        : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, "");
                      formik.setFieldValue(field.name, val);
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched[field.name as keyof typeof formik.values] &&
                    formik.errors[field.name as keyof typeof formik.values] && (
                      <div className="text-danger mt-1">
                        {
                          formik.errors[
                            field.name as keyof typeof formik.values
                          ]
                        }
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <h3 className="fs-4 fw-bold text-black mb-md-3 mb-2">
        Component / Product
      </h3>
      <div className="card border-0 rounded-4 mb-md-4 mb-3 shadow-sm">
        <div className="card-body p-md-4 p-3">
          <div className="row g-md-4 g-3">
            {componentProducts.map((field) => (
              <div className="col-lg-4 col-md-6" key={field.name}>
                <div className="form-item">
                  <label className="custom-label mb-1 ff">{field.label}</label>
                  <Input
                    name={field.name}
                    className="form-control form-control-lg"
                    value={
                      formik.values[field.name as keyof typeof formik.values]
                    }
                    placeholder="Target / Hour"
                    prefix={<span className="text-white">Target/Hour</span>}
                    size="large"
                    status={
                      formik.touched[
                        field.name as keyof typeof formik.values
                      ] &&
                      formik.errors[field.name as keyof typeof formik.values]
                        ? "error"
                        : ""
                    }
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, "");
                      formik.setFieldValue(field.name, val);
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched[field.name as keyof typeof formik.values] &&
                    formik.errors[field.name as keyof typeof formik.values] && (
                      <div className="text-danger mt-1">
                        {
                          formik.errors[
                            field.name as keyof typeof formik.values
                          ]
                        }
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-center align-items-center gap-3 mt-4 mb-5 flex-shrink-0">
        <button
          type="button"
          className="btn btn-lg btn-outline-secondary mob-btn px-4"
          onClick={() => {
            navigate(-1);
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={saving}
          className="btn btn-lg btn-primary mob-btn px-5"
          onClick={() => formik.handleSubmit()}
        >
          {saving ? <BtnLoader /> : "Update"}
        </button>
      </div>
      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 z-3">
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export default UpdatePriceSheetVariables;
